import React, { useContext } from 'react';

import Badge from './Badge';
import Box from '../../../components/Box/Box';
import Icon from '../../../components/Icon/Icon';
import { Link } from '../../../components/Link';
import { PrimaryButton, ButtonIcon } from '../../../components/Button';
import Cart from './Cart';

import HeaderNavBarContext from '../context/HeaderNavBarContext';

import UserDropdownMenu from './UserDropdownMenu';
import BadgeCounter from './BadgeCounter';

import fireEvent from '../utils/fireEvent';
import useTarget from '../hooks/useTarget';
import type { TMenuLink } from 'src/types/TMenuLink';

type TUserLinks = {
  showHamburgerMenu: () => void;
  menuLinks?: TMenuLink[];
};

const UserLinks = ({ showHamburgerMenu, menuLinks }: TUserLinks) => {
  const loginTarget = useTarget('');

  const { userDetails, userLinks, ssoLinks, locale } = useContext<any>(HeaderNavBarContext);
  const logoLocale = locale.toLowerCase().replace(/\W/g, '');
  const colorInverse = logoLocale === 'nlnl';

  const { isLoggedIn } = userDetails;

  const handlePlaceAd = (event) => {
    const placeAdEvent = fireEvent(
      'placead',
      {},
      {
        cancelable: true,
        node: event.target,
      },
    );

    if (placeAdEvent.defaultPrevented) {
      event.preventDefault();
    }
  };

  return (
    <Box as="ul" parent="Header" name="menu" data-role="nav-dropdown-menu">
      {userLinks.messages && (
        <li className="hz-hide-custom--messages">
          <Link
            href={userLinks.messages.url}
            data-sem="messagingDisplayAction"
            data-role={userLinks.messages.role}
            title={userLinks.messages.text}
            tabIndex={0}
          >
            <Box as="div" parent="Button" name="badge">
              <Icon name="messages" color={colorInverse ? 'background-default-inverse' : 'background-default'} />
              {isLoggedIn && (
                <BadgeCounter
                  endpoint={userLinks.messages.badge.endpoint}
                  badgeKey={userLinks.messages.badge.key}
                  badgeName={userLinks.messages.badge.name}
                />
              )}
              <span className="hz-hide-lte-md">{userLinks.messages.text}</span>
            </Box>
          </Link>
        </li>
      )}

      {userLinks.notifications && (
        <li>
          <Link
            role="button"
            href={userLinks.notifications.url}
            data-role={userLinks.notifications.role}
            title={userLinks.notifications.text}
            tabIndex={0}
          >
            <Box as="div" parent="Button" name="badge">
              <Icon name="notification" color={colorInverse ? 'background-default-inverse' : 'background-default'} />
              {isLoggedIn && (
                <BadgeCounter
                  endpoint={userLinks.notifications.badge.endpoint}
                  badgeKey={userLinks.notifications.badge.key}
                  badgeName={userLinks.notifications.badge.name}
                />
              )}
              {!isLoggedIn && <Badge kind="signalAction" />}
              <span className="hz-hide-lte-md">{userLinks.notifications.text}</span>
            </Box>
          </Link>
        </li>
      )}

      {isLoggedIn && userLinks.shoppingCart && <Cart colorInverse={colorInverse} cart={userLinks.shoppingCart} />}

      {!isLoggedIn && (
        <>
          <li className="hz-hide-gte-lg">
            <Link data-role="hamburgerOpen" onClick={showHamburgerMenu} tabIndex={-1}>
              <Icon name="profile" color={colorInverse ? 'background-default-inverse' : 'background-default'} />
            </Link>
          </li>

          <li className="hz-hide-lte-md">
            <Link href={`${ssoLinks.login.url}?target=${loginTarget}`} data-role={ssoLinks.login.role} tabIndex={0}>
              <Icon name="profile" color={colorInverse ? 'background-default-inverse' : 'background-default'} />
              <span>{ssoLinks.login.text}</span>
            </Link>
          </li>
        </>
      )}

      {isLoggedIn && (
        <UserDropdownMenu showHamburgerMenu={showHamburgerMenu} menuLinks={menuLinks} colorInverse={colorInverse} />
      )}

      {userLinks.placeAd && (
        <li>
          <PrimaryButton
            as="a"
            href={userLinks.placeAd.url}
            onClick={handlePlaceAd}
            className="hz-Button--callToAction"
            size="small"
            data-role={userLinks.placeAd.role}
            tabIndex={0}
          >
            <ButtonIcon name="pin-callToAction-foreground" isStartIcon />
            &nbsp;
            <span className="hz-hide-lte-md">{userLinks.placeAd.text}</span>
          </PrimaryButton>
        </li>
      )}

      {userLinks.localeAlt && (
        <li className="hz-hide-lte-md">
          <Link href={userLinks.localeAlt.url} data-role={userLinks.localeAlt.role} tabIndex={0}>
            {userLinks.localeAlt.text}
          </Link>
        </li>
      )}
    </Box>
  );
};

export default UserLinks;
